/**
 * MicButton — speech-to-text via the browser's Web Speech API.
 *
 * Permission flow (Feb 2026 hardening):
 *   1. Probe `navigator.mediaDevices.getUserMedia({ audio: true })` BEFORE
 *      starting SpeechRecognition. Some Chromium builds silently fail when
 *      `SpeechRecognition.start()` is called without a prior explicit
 *      permission grant — especially inside iframes.
 *   2. Distinguish between:
 *        • NotAllowedError / SecurityError   → permission denied / iframe-blocked
 *        • NotFoundError / OverconstrainedError → no microphone device
 *        • AbortError / other                  → generic transient failure
 *   3. Publish a visible status pill: "Mic ready" · "Mic blocked" · "Mic not detected"
 *   4. When loaded inside an iframe that lacks `allow="microphone"`, surface a
 *      dedicated warning.
 *
 * Privacy: the browser handles transcription; raw audio is never stored or
 * sent to our backend. The student must edit + submit the transcript
 * explicitly for it to leave the device.
 */
import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useAccessibility } from "@/contexts/AccessibilityContext";

const SR =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

// Detect if we're running inside an iframe. Used only to offer the user an
// "Open in new tab" escape hatch WHEN getUserMedia actually fails — we no
// longer block proactively, because `featurePolicy.allowsFeature` is
// unreliable across browsers and the definitive signal is the real
// getUserMedia attempt.
function isInIframe() {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;  // cross-origin top frame throws → definitely iframed
  }
}

// --- status values ---
// "unknown"       — haven't probed yet
// "ready"         — getUserMedia resolved on last probe
// "blocked"       — NotAllowedError / SecurityError (user denied or iframe)
// "not-detected"  — NotFoundError / OverconstrainedError
// "unsupported"   — browser has no SpeechRecognition API at all
// "iframe"        — running inside an iframe without mic permission
// "error"         — transient other error

export default function MicButton({
  onTranscript,
  inputId,
  testId = "mic-btn",
  language = "en-US",
  className = "",
  size = "md",
}) {
  const { settings } = useAccessibility();
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [status, setStatus] = useState(!SR ? "unsupported" : "unknown");
  const [errorMsg, setErrorMsg] = useState("");
  const recRef = useRef(null);

  // ============== PASSIVE PROBE ON MOUNT ==============
  // Uses the Permissions API where available to tell the user up-front
  // whether the mic is ready — without forcing the permission prompt.
  // NOTE: we intentionally do NOT proactively block based on iframe detection
  // here. Some modern browsers fully support mic in iframes (Emergent's
  // preview included, once the outer iframe adds `allow=microphone`), and
  // `featurePolicy.allowsFeature` is unreliable. The definitive signal is
  // the real getUserMedia() call below.
  useEffect(() => {
    if (!SR) return;
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const p = await navigator.permissions.query({ name: "microphone" });
          if (cancelled) return;
          if (p.state === "granted") setStatus("ready");
          else if (p.state === "denied") {
            setStatus("blocked");
            setErrorMsg("Microphone access is blocked in your browser settings.");
          }
          p.onchange = () => {
            if (p.state === "granted") { setStatus("ready"); setErrorMsg(""); }
            else if (p.state === "denied") {
              setStatus("blocked");
              setErrorMsg("Microphone access is blocked in your browser settings.");
            }
          };
        }
      } catch {
        /* Permissions API not available — stay "unknown" until first click */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cleanup any active session on unmount
  useEffect(() => () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
  }, []);

  if (!SR || !settings.speechInputEnabled) return null;

  // ============== ACTIVE PERMISSION PROBE + START ==============
  const start = async () => {
    setErrorMsg("");
    setInterim("");

    // 1) Force an explicit permission prompt — this is what was missing before.
    //    SpeechRecognition in Chromium can silently fail without this call.
    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus("unsupported");
      setErrorMsg("Your browser doesn't expose a microphone API (need HTTPS on a modern browser).");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Immediately stop the tracks — SpeechRecognition owns its own stream.
      // We only needed the permission grant.
      stream.getTracks().forEach((t) => t.stop());
      setStatus("ready");
    } catch (err) {
      // Always log the raw error so the user/dev can see it in DevTools.
      console.error("[MicButton] getUserMedia failed:", err?.name, err?.message, err);
      const name = err?.name || "";
      const iframed = isInIframe();
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        // In an iframe, NotAllowedError almost always means the parent didn't
        // grant `allow=microphone`. Surface the iframe-specific guidance so
        // users know to open the app in a new tab.
        if (iframed) {
          setStatus("iframe");
          setErrorMsg(
            "This preview frame doesn't allow microphone access. " +
            "Open the app in a new tab to use voice typing."
          );
        } else {
          setStatus("blocked");
          setErrorMsg(
            "Microphone access denied. Click the 🔒 in your address bar, " +
            "allow microphone for this site, and try again."
          );
        }
      } else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
        setStatus("not-detected");
        setErrorMsg("No microphone detected. Please connect a microphone and try again.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setStatus("error");
        setErrorMsg("Another app is using the microphone. Close it and try again.");
      } else {
        setStatus("error");
        setErrorMsg(`Couldn't access the microphone (${name || "unknown error"}).`);
      }
      return;
    }

    // 2) Now safe to start SpeechRecognition.
    let rec;
    try {
      rec = new SR();
      rec.lang = language;
      rec.continuous = false;
      rec.interimResults = true;
      rec.maxAlternatives = 1;
    } catch (e) {
      console.error("[MicButton] SpeechRecognition constructor failed:", e);
      setStatus("error");
      setErrorMsg("Could not start speech recognition.");
      return;
    }

    rec.onresult = (event) => {
      let finalText = "";
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const r = event.results[i];
        if (r.isFinal) finalText += r[0].transcript;
        else interimText += r[0].transcript;
      }
      if (interimText) setInterim(interimText);
      if (finalText) {
        onTranscript?.(finalText.trim() + " ", { isFinal: true });
        setInterim("");
      }
    };
    rec.onerror = (e) => {
      console.error("[MicButton] SpeechRecognition error:", e.error, e);
      const map = {
        "not-allowed": "Microphone access blocked. Please allow it in your browser.",
        "service-not-allowed": "Microphone access blocked by the browser.",
        "no-speech": "Didn't hear anything. Try again.",
        "audio-capture": "No microphone detected. Please connect one and try again.",
        network: "Network issue. Check your connection.",
      };
      if (e.error === "not-allowed" || e.error === "service-not-allowed") setStatus("blocked");
      else if (e.error === "audio-capture") setStatus("not-detected");
      setErrorMsg(map[e.error] || "Microphone error. Try again.");
      setListening(false);
    };
    rec.onend = () => setListening(false);

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch (e) {
      console.error("[MicButton] rec.start() threw:", e);
      setErrorMsg("Microphone is already running.");
    }
  };

  const stop = () => {
    try { recRef.current?.stop(); } catch { /* ignore */ }
    setListening(false);
  };

  const sizeClasses =
    size === "sm" ? "w-8 h-8" : size === "lg" ? "w-12 h-12" : "w-10 h-10";

  // ------- tiny status pill -------
  const statusPill = (() => {
    if (status === "ready") return { text: "Mic ready", color: "text-emerald-700", icon: CheckCircle2, testId: "mic-status-ready" };
    if (status === "blocked") return { text: "Mic blocked", color: "text-red-700", icon: AlertTriangle, testId: "mic-status-blocked" };
    if (status === "not-detected") return { text: "Mic not detected", color: "text-amber-700", icon: AlertTriangle, testId: "mic-status-not-detected" };
    if (status === "iframe") return { text: "Mic blocked (restricted frame)", color: "text-red-700", icon: AlertTriangle, testId: "mic-status-iframe" };
    if (status === "error") return { text: "Mic error", color: "text-amber-700", icon: AlertTriangle, testId: "mic-status-error" };
    return null; // unknown + unsupported → don't show a pill
  })();
  const StatusIcon = statusPill?.icon;

  return (
    <div className={`relative inline-flex items-center gap-2 ${className}`}>
      <button
        type="button"
        onClick={listening ? stop : start}
        aria-pressed={listening}
        aria-label={
          listening
            ? "Stop voice typing — your words go into the input box"
            : "Start voice typing — your speech turns into text in the input box"
        }
        aria-controls={inputId}
        title={
          listening
            ? "Click to stop. Voice is only used to turn speech into text."
            : "Speak — voice is only used to turn speech into text."
        }
        data-testid={testId}
        className={`${sizeClasses} rounded-full inline-flex items-center justify-center border-2 transition-colors ${
          listening
            ? "bg-red-500 hover:bg-red-600 border-red-600 text-white animate-pulse"
            : status === "blocked" || status === "iframe"
            ? "bg-white hover:bg-red-50 border-red-300 text-red-700"
            : "bg-white hover:bg-purple-50 border-purple-300 text-purple-700"
        }`}
      >
        {listening ? (
          <MicOff className="w-4 h-4" aria-hidden="true" />
        ) : (
          <Mic className="w-4 h-4" aria-hidden="true" />
        )}
      </button>

      {/* Always-visible tiny status pill (when status is known) */}
      {statusPill && !listening && (
        <span
          className={`inline-flex items-center gap-1 text-[11px] font-semibold ${statusPill.color}`}
          data-testid={statusPill.testId}
        >
          <StatusIcon className="w-3.5 h-3.5" aria-hidden="true" />
          {statusPill.text}
        </span>
      )}

      {(listening || interim || errorMsg) && (
        <div
          className="absolute z-30 bottom-full mb-2 left-0 min-w-[260px] max-w-xs bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs"
          role="status"
          aria-live="polite"
          data-testid="mic-tooltip"
        >
          {errorMsg ? (
            <>
              <p className="text-red-700" data-testid="mic-error-message">{errorMsg}</p>
              {(status === "iframe" || (status === "blocked" && isInIframe())) && (
                <a
                  href={typeof window !== "undefined" ? window.location.href : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-bold rounded-md no-underline"
                  data-testid="mic-open-new-tab-btn"
                >
                  ↗ Open in new tab
                </a>
              )}
            </>
          ) : listening ? (
            <>
              <p className="font-semibold text-purple-700 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
                Listening…
              </p>
              <p className="mt-0.5 text-gray-700 italic min-h-[1em]">
                {interim || "Speak whenever you're ready."}
              </p>
              <p className="mt-1 text-[10px] text-gray-400">
                Your voice is only used to turn speech into text. Edit before sending.
              </p>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export const isSpeechRecognitionSupported = () => Boolean(SR);
